// Avatar dropdown — name + workspace + agents/billing/threads + logout.
// Adapted from inku/frontend/web/src/components/common/UserMenu.tsx.

import {
  CreditCard,
  LogOut,
  MessageSquare,
  RotateCcw,
  Sparkles,
  User as UserIcon,
} from "lucide-react";

import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useStore } from "@/lib/store";

function initials(name: string | undefined): string {
  if (!name) return "I";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function UserMenu() {
  const { me, navigate, resetAll, signOut, threads, agents } = useStore();

  const handleReset = () => {
    if (
      !confirm(
        "Reset everything? This wipes:\n- All threads, messages, artifacts (localStorage)\n- All server-side CD sessions and CDBriefs\n\nThe page will reload."
      )
    )
      return;
    resetAll();
  };

  const handleLogout = () => {
    signOut();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring/50">
          <Avatar className="h-8 w-8">
            <AvatarFallback className="bg-foreground text-background text-xs font-medium">
              {initials(me?.name)}
            </AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium">{me?.name ?? "Guest"}</span>
            <span className="text-xs text-muted-foreground">
              {me?.org ?? "Workspace"}
            </span>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => navigate("/")}
          className="cursor-pointer flex items-center justify-between"
          disabled
        >
          <span className="flex items-center">
            <MessageSquare className="mr-2 h-4 w-4" />
            Threads
          </span>
          <span className="text-xs text-muted-foreground">{threads.length}</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => navigate("/agents")}
          className="cursor-pointer flex items-center justify-between"
        >
          <span className="flex items-center">
            <Sparkles className="mr-2 h-4 w-4" />
            Scheduled agents
          </span>
          <span className="text-xs text-muted-foreground">{agents.length}</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => navigate("/billing")}
          className="cursor-pointer"
        >
          <CreditCard className="mr-2 h-4 w-4" />
          Billing
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => navigate("/settings")}
          className="cursor-pointer"
        >
          <UserIcon className="mr-2 h-4 w-4" />
          Profile
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={handleReset}
          className="cursor-pointer text-destructive focus:text-destructive"
        >
          <RotateCcw className="mr-2 h-4 w-4" />
          Reset all (dev)
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={handleLogout} className="cursor-pointer">
          <LogOut className="mr-2 h-4 w-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
